import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiEndpointService } from 'app/core/services/api-endpoint.service';
import { ApiHealthService } from 'app/core/services/api-health.service';
import { IMockRegistry } from 'app/mocks/interfaces/mock-registry.interface';
import { mockRegistryFactories } from 'app/mocks/mock-registry-factories';
import { environment } from 'environments/environment';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

@Injectable()
export class MockHttpInterceptor implements HttpInterceptor {
    private readonly mockRegistry: IMockRegistry[] = [];
    private cache = new Map<string, unknown>();

    public constructor(private readonly apiHealthService: ApiHealthService,
                       private readonly apiEndpointService: ApiEndpointService,
    ) {
        this.mockRegistry = mockRegistryFactories.map(factory => factory(this.apiEndpointService, this));
    }

    public intercept(
        req: HttpRequest<unknown>,
        next: HttpHandler,
    ): Observable<HttpEvent<unknown>> {
        // 1) Always forward HEAD/OPTIONS (for auth, CORS, health‑checks)
        if (req.method === 'HEAD' || req.method === 'OPTIONS') {
            return next.handle(req);
        }

        // 2) If mocks globally disabled, forward everything
        if (!environment.useMocks) {
            return next.handle(req);
        }

        // 3) Find a matching mock entry
        const urlPath = this.getPathFromUrl(req.url);
        const entry = this.mockRegistry.find(
            r => r.method === req.method && !!r.matcher(urlPath),
        );

        if (!entry) {
            // no mock defined → forward
            return next.handle(req);
        }

        const matchResult = entry.matcher(urlPath);
        if (!matchResult) {
            return next.handle(req); // Extra guard for safety
        }

        // 4) Only mock if the endpoint was marked “down” at startup
        const shouldUseMock = environment.useMocks && this.apiHealthService.shouldMock(req.url);
        if (!shouldUseMock) {
            return next.handle(req);
        }

        // 5) Extract named params from the URL
        const params = matchResult.params;

        // 6) Compute a cache key for GET vs. mutations
        const key = `${ req.method }::${ req.urlWithParams }`;

        // --- Handle GET ---
        if (req.method === 'GET') {
            // first GET → generate & cache
            if (!this.cache.has(key)) {
                const resp = entry.handler(req, params);
                this.cache.set(key, resp.body);
                return of(resp).pipe(delay(200 + Math.random() * 200));
            }

            // subsequent GETs → return the same reference
            return of(
                new HttpResponse({ status: 200, body: this.cache.get(key) }),
            ).pipe(delay(200 + Math.random() * 200));
        }

        // --- Handle mutations (POST/PUT/DELETE) ---
        // find or init the “current” state
        const getKey = `GET::${ req.urlWithParams }`;
        let current = this.cache.get(getKey);
        if (current === undefined) {
            const getKey = `GET::${ req.urlWithParams }`;
            const getEntry = this.mockRegistry.find(
                r => r.method === 'GET' && r.matcher(new URL(req.url).pathname),
            );

            if (getEntry) {
                const getResp = getEntry.handler(req, params);
                current = getResp.body;
                this.cache.set(getKey, current); // optional: cache it for future GETs
            }
        }

        // call the handler with current state + payload
        const updatedResp = entry.handler(req, params, current, req.body);

        // update cache so future GETs see the change
        this.cache.set(getKey, updatedResp.body);

        return of(updatedResp).pipe(delay(200 + Math.random() * 200));
    }

    public getAllCache(): Map<string, unknown> {
        return this.cache;
    }

    public getCache<T>(url: string): T | undefined {
        const key = `GET::${ url }`;
        return this.cache.get(key) as T | undefined;
    }

    public setCache<T>(url: string, value: T): void {
        const key = `GET::${ url }`;
        this.cache.set(key, value);
    }

    private getPathFromUrl(url: string): string {
        try {
            return new URL(url).pathname;
        } catch {
            return url.split('?')?.[0];
        }
    }
}
